import React, { useCallback, useRef, useState } from "react";
import Cleave from "cleave.js/react";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, FormFeedback, Input, Label, Row, Spinner } from "reactstrap";
import {
  AddressLookupData,
  lookupAddressByPostalCode,
} from "../../../../services/addressLookupService";
import { ClientServicePreferencesFormValues } from "../../../../helpers/clientServicePreferences";
import {
  ClientDocumentType,
  ClientGender,
} from "../../../../services/clientService";

export interface ClientFormValues {
  name: string;
  phone_display: string;
  secondary_phone_display: string;
  email: string | null;
  birth_date: string | null;
  document_type: ClientDocumentType;
  document_number_display: string;
  gender: ClientGender | "";
  instagram_handle: string | null;
  acquisition_source: string | null;
  allow_online_booking: boolean;
  source_registered_on: string | null;
  /** UI positiva: recebe = true → opt_out false na API. */
  accepts_appointment_email: boolean;
  accepts_marketing_email: boolean;
  accepts_sms_marketing: boolean;
  street: string | null;
  number: string | null;
  neighborhood: string | null;
  complement: string | null;
  city: string | null;
  state: string | null;
  postal_code_display: string;
  country: string | null;
  notes: string | null;
  /** Structured prefs form slice (GAP-F2-065). */
  service_preferences: ClientServicePreferencesFormValues;
}

interface ClientAddressFieldsProps {
  formik: FormikProps<ClientFormValues>;
}

const ClientAddressFields: React.FC<ClientAddressFieldsProps> = ({ formik }) => {
  const { t } = useTranslation();
  const [lookupLoading, setLookupLoading] = useState(false);
  const lastLookupRef = useRef("");

  const fieldError = (field: keyof ClientFormValues) =>
    formik.touched[field] && formik.errors[field] ? String(formik.errors[field]) : null;

  const hasExistingAddress = useCallback((): boolean => {
    const { street, city } = formik.values;

    return Boolean(street?.trim() || city?.trim());
  }, [formik.values]);

  const applyAddressData = useCallback(
    (data: AddressLookupData) => {
      formik.setFieldValue("street", data.street || null);
      formik.setFieldValue("neighborhood", data.neighborhood || null);
      formik.setFieldValue("city", data.city || null);
      formik.setFieldValue("state", data.state || null);
    },
    [formik]
  );

  const lookupPostalCode = useCallback(
    async (rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "");

      if (digits.length !== 8) {
        return;
      }

      if (lastLookupRef.current === digits) {
        return;
      }

      lastLookupRef.current = digits;
      setLookupLoading(true);

      try {
        const response = await lookupAddressByPostalCode(digits);

        if (!response.success || !response.data) {
          toast.error(t("studio.clients.address.lookup_failed"));
          return;
        }

        if (hasExistingAddress()) {
          const confirmed = window.confirm(t("studio.clients.address.replace_confirm"));

          if (!confirmed) {
            return;
          }
        }

        applyAddressData(response.data);
      } catch {
        toast.error(t("studio.clients.address.lookup_failed"));
      } finally {
        setLookupLoading(false);
      }
    },
    [applyAddressData, hasExistingAddress, t]
  );

  const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    formik.setFieldValue("postal_code_display", value);

    const digits = value.replace(/\D/g, "");

    if (digits.length === 8) {
      lookupPostalCode(value);
    } else {
      lastLookupRef.current = "";
    }
  };

  const handlePostalCodeBlur = () => {
    formik.setFieldTouched("postal_code_display", true);
    lookupPostalCode(formik.values.postal_code_display);
  };

  return (
    <div className="mt-2">
      <h6 className="text-muted text-uppercase fs-12 mb-3">
        {t("clients.form.section.address")}
      </h6>

      <Row className="g-3">
        <Col md={6}>
          <Label htmlFor="client-postal-code">{t("clients.fields.postalCode.label")}</Label>
          <div className="position-relative">
            <Cleave
              id="client-postal-code"
              className={`form-control${fieldError("postal_code_display") ? " is-invalid" : ""}`}
              placeholder={t("clients.fields.postalCode.placeholder")}
              options={{
                delimiters: ["-"],
                blocks: [5, 3],
                numericOnly: true,
              }}
              value={formik.values.postal_code_display}
              onChange={handlePostalCodeChange}
              onBlur={handlePostalCodeBlur}
              disabled={lookupLoading}
            />
            {lookupLoading ? (
              <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                <Spinner
                  size="sm"
                  color="primary"
                  aria-label={t("studio.clients.address.lookup_loading")}
                />
              </div>
            ) : null}
          </div>
          {fieldError("postal_code_display") ? (
            <div className="invalid-feedback d-block">{fieldError("postal_code_display")}</div>
          ) : null}
        </Col>

        <Col lg={8}>
          <Label htmlFor="client-street">{t("clients.fields.street.label")}</Label>
          <Input
            id="client-street"
            name="street"
            placeholder={t("clients.fields.street.placeholder")}
            value={formik.values.street ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("street")}
          />
          {fieldError("street") ? (
            <FormFeedback type="invalid">{fieldError("street")}</FormFeedback>
          ) : null}
        </Col>

        <Col lg={4}>
          <Label htmlFor="client-number">{t("clients.fields.number.label")}</Label>
          <Input
            id="client-number"
            name="number"
            placeholder={t("clients.fields.number.placeholder")}
            value={formik.values.number ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("number")}
          />
          {fieldError("number") ? (
            <FormFeedback type="invalid">{fieldError("number")}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-complement">{t("clients.fields.complement.label")}</Label>
          <Input
            id="client-complement"
            name="complement"
            placeholder={t("clients.fields.complement.placeholder")}
            value={formik.values.complement ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("complement")}
          />
          {fieldError("complement") ? (
            <FormFeedback type="invalid">{fieldError("complement")}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-neighborhood">{t("clients.fields.neighborhood.label")}</Label>
          <Input
            id="client-neighborhood"
            name="neighborhood"
            placeholder={t("clients.fields.neighborhood.placeholder")}
            value={formik.values.neighborhood ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("neighborhood")}
          />
          {fieldError("neighborhood") ? (
            <FormFeedback type="invalid">{fieldError("neighborhood")}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-city">{t("clients.fields.city.label")}</Label>
          <Input
            id="client-city"
            name="city"
            placeholder={t("clients.fields.city.placeholder")}
            value={formik.values.city ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("city")}
          />
          {fieldError("city") ? (
            <FormFeedback type="invalid">{fieldError("city")}</FormFeedback>
          ) : null}
        </Col>

        <Col md={6}>
          <Label htmlFor="client-state">{t("clients.fields.state.label")}</Label>
          <Input
            id="client-state"
            name="state"
            placeholder={t("clients.fields.state.placeholder")}
            value={formik.values.state ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!fieldError("state")}
            maxLength={50}
          />
          {fieldError("state") ? (
            <FormFeedback type="invalid">{fieldError("state")}</FormFeedback>
          ) : null}
        </Col>

        <Input
          type="hidden"
          name="country"
          value={formik.values.country ?? "BR"}
          onChange={formik.handleChange}
        />
      </Row>
    </div>
  );
};

export default ClientAddressFields;
